import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navLinks } from '../../mock/content';
import { Menu, X } from 'lucide-react';
import { track, events } from '../../lib/analytics';

const NavItem = ({ link, onClick, active }) => {
  const cls = `text-[13px] font-bold uppercase tracking-[0.06em] transition-colors ${
    active ? 'text-brand-blue' : 'text-navy hover:text-brand-blue'
  }`;
  if (link.route) {
    return (
      <Link to={link.href} onClick={onClick} className={cls}>
        {link.label}
      </Link>
    );
  }
  return (
    <a href={link.href} onClick={onClick} className={cls}>
      {link.label}
    </a>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const { pathname, hash } = useLocation();

  // Sticky-shadow toggle on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll-spy: highlight the section the user is currently viewing (homepage only)
  useEffect(() => {
    if (pathname !== '/') {
      setActiveSection('');
      return;
    }

    // Pull section ids from hash-style nav links (e.g. "/#how-it-works" -> "how-it-works")
    const ids = navLinks
      .filter((l) => l.href && l.href.startsWith('/#'))
      .map((l) => l.href.slice(2));

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (elements.length === 0) return;

    // Track which sections are currently intersecting; pick the topmost visible one
    const visible = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.boundingClientRect.top);
          } else {
            visible.delete(entry.target.id);
          }
        });
        if (visible.size === 0) {
          setActiveSection('');
          return;
        }
        // Pick the section with the smallest positive top (closest to viewport top)
        const sorted = [...visible.entries()].sort((a, b) => {
          const ta = a[1] < 0 ? Infinity : a[1];
          const tb = b[1] < 0 ? Infinity : b[1];
          return ta - tb;
        });
        setActiveSection(sorted[0][0]);
      },
      {
        // Anchor around the top third of the viewport so the "active" section
        // matches what the user is actually reading, not what's peeking in.
        rootMargin: '-30% 0px -55% 0px',
        threshold: [0, 0.1, 0.5, 1],
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [pathname, hash]);

  const isActive = (link) => {
    // Route-based (e.g. /about)
    if (link.route) return link.href === pathname;
    // Hash-based section anchor on homepage
    if (pathname === '/' && link.href && link.href.startsWith('/#')) {
      return link.href.slice(2) === activeSection;
    }
    return false;
  };

  return (
    <header className="fixed top-4 left-0 right-0 z-50 px-4">
      <div
        className={`max-w-6xl mx-auto transition-all duration-300 rounded-full ${
          scrolled
            ? 'bg-white shadow-mk-lg'
            : 'bg-white/70 backdrop-blur-md shadow-mk-sm'
        }`}
      >
        <div className="h-14 px-6 flex items-center justify-between">
          <Link to="/" className="font-black text-[18px] tracking-tight text-navy select-none">
            5DBS
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map((l) => (
              <NavItem key={l.label} link={l} active={isActive(l)} />
            ))}
            <Link
              to="/contact"
              data-testid="navbar-grab-15-desktop"
              onClick={() => track(events.GRAB_15_CALL_CLICK, { location: 'navbar' })}
              className="inline-flex items-center h-9 px-5 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              Grab 15 min
            </Link>
          </nav>

          <button
            className="md:hidden p-2 text-navy"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-slate-100 px-6 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <NavItem key={l.label} link={l} onClick={() => setOpen(false)} active={isActive(l)} />
            ))}
            <Link
              to="/contact"
              data-testid="navbar-grab-15-mobile"
              onClick={() => { track(events.GRAB_15_CALL_CLICK, { location: 'navbar_mobile' }); setOpen(false); }}
              className="inline-flex items-center justify-center h-9 px-5 rounded-md bg-brand-pink text-white text-[12px] font-bold uppercase tracking-[0.08em] w-fit shadow-mk"
            >
              Grab 15 min
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
